import { Ref, watch } from 'vue';
import { RouterInstance } from '../router';
import { debounce } from 'lodash';

class UrlParams {
  paramKeys: string[] = [];
  queryParameters = new Map<string, any>();

  addParam(param: unknown, key: string, parser: ((queryKeyVal: string | (string | null)[]) => unknown) | null = null) {
    if (RouterInstance.router) {
      watch(
        RouterInstance.data,
        () => {
          if (param && typeof param === 'object') {
            if (assertIsRef(param)) {
              if (RouterInstance.data.value.query[key]) {
                param.value = parseQueryValue(param.value, RouterInstance.data.value.query[key]);
              }
            } else if (Object.keys(param).length > 0) {
              const queryKeys = Object.keys(RouterInstance.data.value.query).filter((queryKey) =>
                queryKey.startsWith(`${key}_`)
              );
              queryKeys.forEach((queryKey) => {
                const queryKeyVal = RouterInstance.data.value.query[queryKey];
                const splitQueryKey = queryKey.split('_');
                if (splitQueryKey.length > 0) {
                  const parsedQueryKey = splitQueryKey[1];
                  if (parsedQueryKey in param) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (param as any)[parsedQueryKey] = parser ? parser(queryKeyVal) : queryKeyVal;
                  }
                }
              });
            }
          }
        },
        { deep: true, immediate: true }
      );
    }

    this.paramKeys.push(key);

    if (param && typeof param === 'object') {
      watch(
        param,
        () => {
          if (assertIsRef(param)) {
            this.queryParameters.set(key, param.value);
          } else if (Object.keys(param).length > 0) {
            for (const [paramKey, value] of Object.entries(param)) {
              this.queryParameters.set(`${key}_${paramKey}`, value);
            }
          }
          this.replaceQueryParamsInRoute();
        },
        { deep: true }
      );
    }
  }

  removeParam(key: string) {
    const index = this.paramKeys.indexOf(key);
    if (index !== -1) {
      this.paramKeys.splice(index, 1);
    }

    this.queryParameters.delete(key);
  }

  private setQueryParamsInRoute() {
    const router = RouterInstance.router;
    if (!router) return;

    const current = router.currentRoute.value;

    const queryParameters: { [key: string]: any } = {};
    this.queryParameters.forEach((v, k) => {
      queryParameters[k] = v;
    });

    const query = { ...current.query, ...queryParameters };

    // remove undefined / empty query params
    Object.keys(query).forEach(
      (key) => (query[key] === null || query[key] === undefined || query[key] === '') && delete query[key]
    );

    const location = current.name
      ? {
          name: current.name,
          params: current.params,
          query
        }
      : {
          path: current.path || '/',
          query
        };

    router.replace(location).catch(() => {
      /**/
    });
  }

  // debounce router replace query params to avoid multiple concurrent router replacements
  private replaceQueryParamsInRoute = debounce(() => {
    this.setQueryParamsInRoute();
  }, 50);
}

const parseQueryValue = (param: unknown, queryValue: string | (string | null)[]) => {
  if (Array.isArray(param) && !Array.isArray(queryValue)) {
    return [queryValue];
  } else if (typeof queryValue === 'string' && !isNaN(Number(queryValue))) {
    return Number(queryValue);
  } else if (
    typeof queryValue === 'string' &&
    (queryValue.toLowerCase() === 'true' || queryValue.toLowerCase() === 'false')
  ) {
    return queryValue.toLowerCase() === 'true';
  } else if (Array.isArray(queryValue)) {
    return (queryValue as string[]).map((value) => parseUrlParamValue(value, true));
  } else {
    return parseUrlParamValue(decodeURI(queryValue as string), true);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const assertIsRef = (param: unknown): param is Ref => typeof param === 'object' && 'value' in (param as any);

export const parseUrlParamValue = (value: string, isQueryParam: boolean) => {
  if (!value) return;
  if (!isQueryParam) {
    value.replaceAll('&', '');
  }
  return value
    .toLowerCase()
    .replaceAll(' ', '-')
    .replaceAll(/-+/g, '-')
    .replaceAll('.', '')
    .replaceAll(',', '')
    .replaceAll('/', '')
    .replaceAll(/[^a-zA-Z0-9-ïáéíóúýçäëïöüÿâêîôûãõñ&@+{}()[_,]/g, '');
};

// create singleton for UrlParams, all url params across different components should be handled in one instance
export const UrlParamsInstance = new UrlParams();
